import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { ButtonComponent, SearchBarComponent } from 'ui-library';

interface Dashboard {
  id: string;
  title: string;
  description: string | undefined;
  projects: Project[];
  allProjects: number;
  otherProjects: number;
}

interface Project {
  id: string;
  name: string;
  dashboards: Array<Dashboard>;
}

@Component({
  selector: 'import-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatAccordion,
    MatExpansionModule,
    MatCheckboxModule,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    MatIconModule,
    SearchBarComponent
  ],
  templateUrl: 'import-dashboard.component.html',
  styleUrls: ['./import-dashboard.component.scss'],
})

export class ImportDashboardComponent implements OnInit, OnDestroy {
  selectedOptions: Dashboard[] = [];
  options: Dashboard[] = [];
  project: Project[] = [];
  destroy$ = new Subject<void>();
  searchTerm: string = '';
  filteredOptions: Dashboard[] = [];
  selectProjects: Project[] = [];
  panelOpenState = false;

  constructor(
    public dialogRef: MatDialogRef<ImportDashboardComponent>,
    private dashboardService: DashboardService,
    private projectService: ProjectService,

    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit() {
    const dashboards$ = this.dashboardService.getDashboardList({ withCharts: false });
    const projects$ = this.projectService.getProjects({});

    if (dashboards$ && projects$) {
      combineLatest([dashboards$, projects$]).pipe(takeUntil(this.destroy$)).subscribe(([dashboards, projects]) => {
        if (!dashboards || !projects) {
          return;
        }

        this.options = dashboards.map((dashboard: any) => {
          const selectProjects = projects.filter((project: any) =>
            dashboard.projects?.includes(project.id)
          );

          const allProjectsLength = dashboard.projects?.length || 0;
          const otherProjectsLength = allProjectsLength - selectProjects.length;

          return {
            id: dashboard.id,
            title: dashboard.title,
            description: dashboard.description,
            projects: selectProjects,
            otherProjects: otherProjectsLength,
            allProjects: allProjectsLength,
          };
        });

        this.filteredOptions = [...this.options];
      });
    } else {
      console.error('Les services ne retournent pas d\'observables valides');
    }
  }

  toggleSelection(dashboard: any) {
    if (this.isSelected(dashboard)) {
      this.selectedOptions = this.selectedOptions.filter(
        (item) => item !== dashboard
      );
    } else {
      this.selectedOptions.push(dashboard);
    }
  }

  isSelected(dashboard: any): boolean {
    return this.selectedOptions.includes(dashboard);
  }

  dismiss(): void {
    this.dialogRef.close(false);
  }

  ok(type: any): void {
    this.dialogRef.close({ dashboards: this.selectedOptions, type });
  }

  private normalizeString(str: string): string {
    return str.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  filterResults(event: string): void {
    this.searchTerm = event;

    if (!this.searchTerm) {
      this.filteredOptions = [...this.options];
    } else {
      const cleanTerm = this.normalizeString(this.searchTerm);

      this.filteredOptions = this.options.filter((dashboard) => {
        const title = this.normalizeString(dashboard.title);
        const description = dashboard.description ? this.normalizeString(dashboard.description) : '';

        const matchesProjects = dashboard.projects.some((project) =>
          this.normalizeString(project.name).includes(cleanTerm)
        );

        return (
          title.includes(cleanTerm) ||
          description.includes(cleanTerm) ||
          matchesProjects
        );
      });
    }
  }

  getPanelState(): boolean {
    return this.panelOpenState;
  }

  getProjectAssociated(dashboard: Dashboard): string {
    if (dashboard.allProjects > 1) {
      return `${dashboard.allProjects} projets associés`;
    } else if (dashboard.allProjects === 1) {
      return `Projet associé : ${dashboard.projects[0]?.name}`;
    } else {
      return 'Aucun projet associé';
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.selectedOptions = [];
    this.options = [];
    this.project = [];
    this.filteredOptions = [];
  }

  cancel() {
    this.dialogRef.close({});
  }
}
